import { useState, useRef, useEffect, useMemo } from 'react'
import { Bell, X, CheckCheck, Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '@hooks/useNotifications'
import { Notification, NotificationType } from '../../types/notification'
import { formatDateTime } from '@utils/format'
import { cn } from '@utils/cn'

const MAX_VISIBLE_NOTIFICATIONS = 10

export function NotificationPanel() {
  const [isOpen, setIsOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { 
    notifications, 
    unreadCount, 
    markAllAsRead, 
    isLoading,
    refetch,
    refetchUnreadCount,
  } = useNotifications()

  // Limitar notificações visíveis para performance
  const visibleNotifications = useMemo(() => {
    return notifications.slice(0, MAX_VISIBLE_NOTIFICATIONS)
  }, [notifications])

  // Atualizar imediatamente ao abrir o painel (sem esperar pelo poll)
  useEffect(() => {
    if (!isOpen) return
    void refetch()
    void refetchUnreadCount()
  }, [isOpen, refetch, refetchUnreadCount])

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleNotificationClick = (notification: Notification) => {
    // NÃO marcar como lida aqui - será marcada quando entrar na folha de obra
    // Navegar para a folha de obra se a notificação estiver relacionada a uma
    if (notification.relatedEntityType === 'WORK_ORDER' && notification.relatedEntityId) {
      navigate(`/work-orders/${notification.relatedEntityId}`)
    }
    
    setIsOpen(false)
  }

  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case NotificationType.WORK_ORDER_COMPLETED:
        return '✅'
      case NotificationType.WORK_ORDER_READY_FOR_PICKUP:
        return '🚗'
      case NotificationType.WORK_ORDER_ASSIGNED:
        return '🔧'
      case NotificationType.WORK_ORDER_CREATED:
        return '📋'
      case NotificationType.WORK_ORDER_UPDATED:
        return '📝'
      default:
        return '🔔'
    }
  }

  return (
    <div className="relative" ref={panelRef}>
      {/* Botão de Notificações */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 hover:bg-gray-700 rounded-lg transition-colors relative"
        aria-label="Notificações"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-semibold rounded-full min-w-[20px] h-5 flex items-center justify-center px-1.5">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Painel de Notificações */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 bg-white rounded-lg shadow-xl w-96 max-h-[600px] flex flex-col z-50 border border-gray-200">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">Notificações</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title="Marcar todas como lidas"
                >
                  <CheckCheck className="w-4 h-4 text-gray-600" />
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded transition-colors"
              >
                <X className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Lista de Notificações */}
          <div 
            className="overflow-y-auto flex-1"
            style={{ maxHeight: '500px' }}
          >
            {isLoading && notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin text-gray-400" />
                <p>A carregar...</p>
              </div>
            ) : visibleNotifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Não há notificações</p>
              </div>
            ) : (
              <>
                <div className="divide-y divide-gray-100">
                  {visibleNotifications.map((notification) => (
                    <button
                      key={notification.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={cn(
                        'w-full text-left p-4 hover:bg-gray-50 transition-colors',
                        !notification.isRead && 'bg-blue-50'
                      )}
                    >
                      <div className="flex items-start gap-3">
                        <span className="text-2xl flex-shrink-0">
                          {getNotificationIcon(notification.type)}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h4
                              className={cn(
                                'font-medium text-sm',
                                !notification.isRead ? 'text-gray-900' : 'text-gray-700'
                              )}
                            >
                              {notification.title}
                            </h4>
                            {!notification.isRead && (
                              <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 mt-1"></span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            {formatDateTime(notification.createdAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
                {notifications.length > MAX_VISIBLE_NOTIFICATIONS && (
                  <div className="p-3 text-center border-t border-gray-200 bg-gray-50">
                    <p className="text-xs text-gray-500">
                      Mostrando {MAX_VISIBLE_NOTIFICATIONS} de {notifications.length} notificações
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
